import React, { useEffect, useState } from 'react';

import { onAuthStateChanged } from 'firebase/auth';

import { useFirebaseAuth } from '../hooks/useFirebaseAuth';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { auth } from '../services/firebase';
import {
  resetUserApiState,
  useLazyGetMyDetails,
} from '../services/redux/api/usersApi';
import { AuthProvider as AuthProviderEnum } from '../shared/constants';
import { useDispatch } from 'react-redux';

enum LoginUserStatus {
  loading = 'loading',
  notLoggedIn = 'notLoggedIn',
  notVerified = 'notVerified',
  loggedIn = 'loggedIn',
  forbidden = 'forbidden',
  maintenance = 'maintenance',
  error = 'error',
  offline = 'offline',
}

const AuthContext = React.createContext<{ loginUserStatus: LoginUserStatus }>({
  loginUserStatus: LoginUserStatus.loading,
});

const getErrorStatusCode = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const errorData = error as {
    status?: number;
    originalStatus?: number;
    response?: { status?: number };
  };

  if (typeof errorData.status === 'number') {
    return errorData.status;
  }

  if (typeof errorData.originalStatus === 'number') {
    return errorData.originalStatus;
  }

  if (typeof errorData.response?.status === 'number') {
    return errorData.response.status;
  }

  return undefined;
};

function AuthProvider(props: { children: React.ReactNode }) {
  const isOnline = useOnlineStatus();
  const [loginUserStatus, setLoginUserStatus] = useState<LoginUserStatus>(
    isOnline ? LoginUserStatus.loading : LoginUserStatus.offline,
  );
  const { signInApi, logOut } = useFirebaseAuth();

  const [
    getMyDetails,
    {
      data: user,
      isError: isGetMyDetailsError,
      isFetching,
      error: myDetailsError,
    },
  ] = useLazyGetMyDetails();

  const dispatch = useDispatch();

  useEffect(() => {
    if (isGetMyDetailsError) {
      if (getErrorStatusCode(myDetailsError) === 403) {
        void logOut();
        return;
      }

      if (getErrorStatusCode(myDetailsError) === 599) {
        setLoginUserStatus(LoginUserStatus.maintenance);
        return;
      }

      setLoginUserStatus(LoginUserStatus.error);
    }
  }, [isGetMyDetailsError, myDetailsError, logOut]);

  useEffect(() => {
    onAuthStateChanged(auth, (user) => {
      if (!isOnline) {
        return;
      }

      if (!user) {
        setLoginUserStatus(LoginUserStatus.notLoggedIn);
      } else if (!user.emailVerified) {
        setLoginUserStatus(LoginUserStatus.notVerified);
      } else {
        setLoginUserStatus(LoginUserStatus.loading);
        dispatch(resetUserApiState());

        signInApi({
          email: user.email,
          providerId: user.providerData[0]?.providerId as AuthProviderEnum,
          uid: user.uid,
          firstname: user.displayName,
        })
          .then(() => {
            getMyDetails();
          })
          .catch((error) => {
            if (getErrorStatusCode(error) === 403) {
              setLoginUserStatus(LoginUserStatus.forbidden);
              return;
            }

            if (getErrorStatusCode(error) === 599) {
              setLoginUserStatus(LoginUserStatus.maintenance);
              return;
            }

            setLoginUserStatus(LoginUserStatus.error);
          });
      }
    });
  }, []);

  useEffect(() => {
    if (user) {
      setLoginUserStatus(LoginUserStatus.loggedIn);
    }
  }, [user, isFetching]);

  useEffect(() => {
    if (!isOnline) {
      setLoginUserStatus(LoginUserStatus.offline);
    }
  }, [isOnline]);

  return (
    <AuthContext.Provider
      value={{
        loginUserStatus: loginUserStatus,
      }}>
      {props.children}
    </AuthContext.Provider>
  );
}

function useAuth() {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return context;
}

export { useAuth, AuthProvider, LoginUserStatus };
